import React, {useRef, useState} from 'react';
import {Button, Dialog, DialogActions, DialogContent, DialogTitle} from '@mui/material';
import FilePicker from 'components/common/FilePicker';
import {useResourceContext} from 'components/record/ResourceContext';
import {useListContext} from 'components/list/ListContext';
import {useMutate} from 'hooks/useFetchData';
import {toast} from 'react-toastify';
import {FormContainer} from 'react-hook-form-mui';
import {TextInput} from 'components/forms/Inputs';
import MapContainer from 'components/common/MapContainer';
import ImageOverlayRotated from 'components/MyEvents/Track/ImageOverlayRotated';
import {SubEventType} from 'types/App';
import {parseKmzFile, ParsedKmz} from 'services/kmzImport';

const ImportMapKMZ = ({subEvent}: {subEvent: SubEventType}) => {
  const {params, resource} = useResourceContext();
  const {refetch} = useListContext();
  const [pickerKey, setPickerKey] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [preview, setPreview] = useState<ParsedKmz | null>(null);
  const previewRef = useRef<ParsedKmz | null>(null);

  const [createMap, createState] = useMutate('CREATE_ONE', resource, {
    context: {
      headers: {
        'apollo-require-preflight': true
      }
    }
  });
  const saving = Boolean(createState && 'loading' in createState ? createState.loading : false);

  const resetPreview = () => {
    if (previewRef.current?.previewUrl) {
      URL.revokeObjectURL(previewRef.current.previewUrl);
    }
    previewRef.current = null;
    setPreview(null);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    resetPreview();
  };

  const handleFileChange: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    const files = e.target.files;
    if (!files?.length) {
      return;
    }
    const file = files[0];
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext !== 'kmz') {
      toast.error('The file is not in correct format!');
      setPickerKey((k) => k + 1);
      return;
    }
    const subEventId = params.subEventId;
    const slug = params.slug;
    if ((subEventId === undefined || subEventId === null) && !slug) {
      toast.error('Missing event.');
      setPickerKey((k) => k + 1);
      return;
    }

    setParsing(true);
    try {
      const parsed = await parseKmzFile(file);
      resetPreview();
      previewRef.current = parsed;
      setPreview(parsed);
      setDialogOpen(true);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Could not read KMZ file.';
      toast.error(message);
    } finally {
      setParsing(false);
      setPickerKey((k) => k + 1);
    }
  };

  const handleCreate = async (form: {name: string}) => {
    const p = previewRef.current;
    if (!p || typeof createMap !== 'function') {
      return;
    }
    try {
      await createMap({
        variables: {
          ...params,
          payload: {
            name: form.name,
            height: p.height,
            width: p.width,
            topLeft: p.topLeft,
            topRight: p.topRight,
            bottomLeft: p.bottomLeft,
            file: p.imageFile
          }
        }
      });
      toast.success('Map has been created successfully!');
      refetch?.();
      handleCloseDialog();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Create failed.';
      toast.error(message);
    }
  };

  const event = subEvent.event;
  const busy = parsing || saving;

  return (
    <React.Fragment>
      <FilePicker
        key={pickerKey}
        label={busy ? 'Working…' : 'Import KMZ'}
        accept=".kmz"
        onFileChange={handleFileChange}
        disabled={busy}
      />

      <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="md">
        <DialogTitle>Import KMZ</DialogTitle>
        <DialogContent>
          {preview && (
            <FormContainer
              FormProps={{id: 'import_kmz_map_form', noValidate: true}}
              defaultValues={{name: preview.suggestedName}}
              onSuccess={handleCreate}
            >
              <TextInput required fullWidth label="Name" name="name" margin="normal" />
              <MapContainer
                center={event.location}
                zoom={event.zoom}
                className="leaflet-crosshair"
                style={{width: '100%', height: 450, position: 'relative', marginTop: 8}}
              >
                <ImageOverlayRotated
                  topLeft={preview.topLeft}
                  topRight={preview.topRight}
                  bottomLeft={preview.bottomLeft}
                  opacity={0.5}
                  image={preview.previewUrl}
                />
              </MapContainer>
            </FormContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" form="import_kmz_map_form" variant="contained" disabled={saving}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default ImportMapKMZ;
